"use client";
import env from "@/env.mjs";
import { convertProductToCartItem } from "@/lib/centra/convertToCartItem/convertToCartItem";
import { AddCustomizationInput } from "@/lib/customization/schemas";
import { useAddCustomization } from "@/lib/hooks/useCustomization/useCustomization";
import useLocation from "@/lib/hooks/useLocation";
import { useCart } from "@/lib/hooks/useSelection";
import { usePatchVariants } from "@/lib/hooks/useSelection/cart/patch";
import { getPatchVariantFromLetter } from "@/lib/patch/utils.client";
import {
  eventAddToCart,
  generateGTagItem,
} from "@/lib/utils/Analytics/Functions";
import { ProductPricesWithDiscount } from "@frend-digital/centra/types/product";
import { PDPProduct } from "../../page";
import { Sizes } from "./ActionSection";

export interface IUseAddToCartProps {
  sizes: Sizes;
  product: PDPProduct;
  setModalOpen: () => void;
  prices: ProductPricesWithDiscount;
  engraving: string;
  patch?: string;
}

export const useAddToCart = ({
  product,
  sizes,
  setModalOpen,
  prices,
  engraving,
  patch,
}: IUseAddToCartProps) => {
  const cart = useCart();
  const { addCustomization, isLoading: isLoadingCustomization } =
    useAddCustomization();
  const { variants } = usePatchVariants();
  const { currency } = useLocation();

  const addToCart = async () => {
    if (
      (product?.comingSoon && product?.comingSoon === "1") ||
      !sizes.selectedSize?.stock
    ) {
      setModalOpen();
      return;
    }
    if (!sizes?.selectedSize) return false;

    if (sizes?.selectedSize?.itemId) {
      cart.addItem.mutate(
        {
          itemId: sizes?.selectedSize?.itemId,
          cartItem: convertProductToCartItem(
            {
              ...product,
              prices,
            },
            sizes?.selectedSize?.itemId,
          ),
        },
        {
          async onSuccess(data) {
            const items = data?.selection?.items;
            const item = items[items.length - 1];

            const customizationItems: AddCustomizationInput["customizationItems"] =
              [];

            if (!!patch) {
              const chosenPatchVariant = getPatchVariantFromLetter(
                variants,
                patch,
              );
              const itemId = chosenPatchVariant?.sizes?.[0]?.itemId; // patches are always one size

              if (itemId) {
                customizationItems.push({
                  customizationItemId: itemId,
                });
              }
            }

            if (!!engraving) {
              customizationItems.push({
                customizationItemId: env.NEXT_PUBLIC_CENTRA_ENGRAVING_ID,
                customizationItemComment: engraving,
              });
            }

            if (customizationItems.length) {
              await addCustomization({
                cartItem: item,
                customizationItems,
              });
            }

            cart.modal.openModal();
          },
        },
      );

      try {
        eventAddToCart({
          currency: currency || "",
          value: prices?.priceAsNumber,
          items: [generateGTagItem(product, prices?.priceAsNumber)],
          additionalData: {
            size: sizes?.selectedSize?.name,
          },
        });
      } catch (e) {
        console.error(e);
      }
    }
  };

  return {
    addToCart,
    isLoading: cart.addItem.isPending || isLoadingCustomization,
  };
};
